import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  PlayCircle,
  PauseCircle,
  Heart,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  MessageCircle,
  Bookmark,
  Trash2,
} from 'lucide-react';
import CommentsPanel from './CommentsPanel';
import LikesPanel from './LikesPanel';

function VideoPlayer({
  content,
  currentUserUuid,
  onNext,
  onPrevious,
  onClose,
  onToggleFavorite,
  onToggleLike,
  onAddComment,
  onDeleteComment,
  onDeleteContent,
  isFavorite = false,
  isLiked = false,
}) {
  const videoRef = useRef(null);
  const secondaryVideoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [error, setError] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [showLikes, setShowLikes] = useState(false);
  const [showHeartBurst, setShowHeartBurst] = useState(false);
  const controlsTimeoutRef = useRef(null);
  const lastTapRef = useRef(0);

  const isImage = content.type === 'image';
  const videoSrc = content.primaryUrl || content.url || '';
  const secondarySrc = content.secondaryUrl || '';
  const isOwn = content.userUuid === currentUserUuid;

  const resetControlsTimeout = useCallback(() => {
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    setShowControls(true);
    if (!isImage) {
      controlsTimeoutRef.current = setTimeout(() => {
        if (isPlaying) setShowControls(false);
      }, 3000);
    }
  }, [isPlaying, isImage]);

  useEffect(() => {
    setProgress(0);
    setCurrentTime(0);
    setIsPlaying(!isImage);
    setError(null);
    setShowComments(false);
    setShowLikes(false);
    setShowControls(true);

    return () => {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, isImage]);

  useEffect(() => {
    if (isImage) return;
    const play = (ref) => {
      if (ref.current) {
        if (isPlaying) {
          ref.current.play().catch(() => {});
        } else {
          ref.current.pause();
        }
      }
    };
    play(videoRef);
    play(secondaryVideoRef);
  }, [isPlaying, isImage]);

  const handleInteraction = () => resetControlsTimeout();

  const togglePlayPause = () => {
    if (isImage) return;
    setIsPlaying(!isPlaying);
    handleInteraction();
  };

  const handleDoubleTap = (e) => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      e.stopPropagation();
      onToggleLike();
      setShowHeartBurst(true);
      setTimeout(() => setShowHeartBurst(false), 700);
    }
    lastTapRef.current = now;
  };

  const handleProgressUpdate = () => {
    if (videoRef.current && duration > 0) {
      setProgress((videoRef.current.currentTime / duration) * 100);
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percentage = (e.clientX - rect.left) / rect.width;
    const newTime = percentage * duration;
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
      if (secondaryVideoRef.current) secondaryVideoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
      setProgress(percentage * 100);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
    handleInteraction();
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
    handleInteraction();
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const skip = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime += seconds;
      if (secondaryVideoRef.current) secondaryVideoRef.current.currentTime = videoRef.current.currentTime;
      handleInteraction();
    }
  };

  const handleDelete = () => {
    if (window.confirm('Delete this content? This cannot be undone.')) {
      onDeleteContent?.();
    }
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-black z-50"
      onClick={handleInteraction}
      onMouseMove={handleInteraction}
      onTouchStart={handleInteraction}
    >
      {/* Primary Content: Video or Image */}
      {isImage ? (
        <img
          src={videoSrc}
          alt={content.question?.summary || ''}
          className="w-full h-full object-contain"
          onClick={(e) => handleDoubleTap(e)}
          onError={() => setError('Failed to load image')}
        />
      ) : (
        <video
          ref={videoRef}
          src={videoSrc}
          className="w-full h-full object-contain"
          onClick={(e) => {
            handleDoubleTap(e);
            togglePlayPause();
          }}
          onTimeUpdate={handleProgressUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={() => setIsPlaying(false)}
          onWaiting={() => setIsBuffering(true)}
          onPlaying={() => setIsBuffering(false)}
          onError={() => setError('Failed to load video')}
          autoPlay
          playsInline
        />
      )}

      {/* Secondary Video (PiP) — only for videos */}
      {!isImage && secondarySrc && (
        <div className="absolute bottom-24 right-4 w-[30%] max-w-[175px] rounded-xl overflow-hidden border border-white/20 shadow-xl">
          <video
            ref={secondaryVideoRef}
            src={secondarySrc}
            className="w-full h-auto"
            muted
            playsInline
            autoPlay
          />
        </div>
      )}

      {/* Heart Burst Animation */}
      {showHeartBurst && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
          <Heart className="w-20 h-20 text-red-500 fill-red-500 animate-heart-burst" />
        </div>
      )}

      {/* Loading Spinner */}
      {!isImage && isBuffering && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="text-center">
            <p className="text-white text-lg mb-4">{error}</p>
            <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white">
              Close
            </button>
          </div>
        </div>
      )}

      {/* Controls Overlay */}
      <div className={`absolute inset-0 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        {/* Top Bar */}
        <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/70 to-transparent">
          <div className="flex justify-between items-start">
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
              <ChevronLeft className="w-7 h-7 text-white" />
            </button>
            <div className="text-white text-center flex-1 px-4">
              <p className="font-semibold">{content.userName}</p>
              <p className="text-sm opacity-80">{content.question?.summary}</p>
            </div>
            <div className="flex items-center gap-1">
              {isOwn && onDeleteContent && (
                <button onClick={handleDelete} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                  <Trash2 className="w-5 h-5 text-red-400" />
                </button>
              )}
              {!isImage && (
                <button onClick={toggleFullscreen} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                  {isFullscreen ? <Minimize className="w-6 h-6 text-white" /> : <Maximize className="w-6 h-6 text-white" />}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Previous / Next chevrons */}
        <button onClick={onPrevious} className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/40 hover:bg-black/60 rounded-full transition-colors pointer-events-auto">
          <ChevronLeft className="w-7 h-7 text-white" />
        </button>
        <button onClick={onNext} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/40 hover:bg-black/60 rounded-full transition-colors pointer-events-auto">
          <ChevronRight className="w-7 h-7 text-white" />
        </button>

        {/* Center Play/Pause — only for video */}
        {!isImage && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <button onClick={togglePlayPause} className="p-4 hover:bg-white/20 rounded-full transition-colors pointer-events-auto">
              {isPlaying ? <PauseCircle className="w-16 h-16 text-white" /> : <PlayCircle className="w-16 h-16 text-white" />}
            </button>
          </div>
        )}

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
          {/* Question */}
          <div className="mb-3 px-1">
            <p className="text-white font-semibold">{content.question?.question}</p>
            <p className="text-gray-300 text-xs mt-1">
              {content.question?.questionCategory}
              {content.creation && ` \u00B7 ${new Date(content.creation).toLocaleDateString()}`}
            </p>
          </div>

          {/* Engagement Row */}
          <div className="flex items-center gap-4 mb-3">
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => { onToggleLike(); handleInteraction(); }}
                className="hover:bg-white/10 p-1 rounded-lg transition-colors"
              >
                <Heart className={`w-6 h-6 transition-colors ${isLiked ? 'text-red-500 fill-red-500' : 'text-white'}`} />
              </button>
              {(content.likeCount || 0) > 0 && (
                <button
                  onClick={() => { setShowLikes(true); handleInteraction(); }}
                  className="text-white text-sm hover:text-gray-300 transition-colors"
                >
                  {content.likeCount}
                </button>
              )}
            </div>
            <button
              onClick={() => { setShowComments(true); handleInteraction(); }}
              className="flex items-center gap-1.5 hover:bg-white/10 px-2 py-1 rounded-lg transition-colors"
            >
              <MessageCircle className="w-6 h-6 text-white" />
              {(content.commentCount || 0) > 0 && <span className="text-white text-sm">{content.commentCount}</span>}
            </button>
            <button
              onClick={() => { onToggleFavorite(); handleInteraction(); }}
              className="flex items-center gap-1.5 hover:bg-white/10 px-2 py-1 rounded-lg transition-colors"
            >
              <Bookmark className={`w-6 h-6 transition-colors ${isFavorite ? 'text-yellow-400 fill-yellow-400' : 'text-white'}`} />
            </button>
          </div>

          {/* Progress Bar — only for video */}
          {!isImage && (
            <>
              <div className="mb-2">
                <div
                  className="w-full bg-white/20 h-1 rounded-full cursor-pointer hover:h-2 transition-all"
                  onClick={handleSeek}
                >
                  <div className="bg-lime-300 h-full rounded-full" style={{ width: `${progress}%` }} />
                </div>
                <div className="flex justify-between text-white text-xs mt-1 px-1">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Controls Row */}
              <div className="flex items-center justify-between">
                <button onClick={toggleMute} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                  {isMuted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
                </button>
                <div className="flex items-center space-x-2">
                  <button onClick={() => skip(-10)} className="px-3 py-1 text-white text-sm hover:bg-white/20 rounded-lg transition-colors">
                    -10s
                  </button>
                  <button onClick={() => skip(10)} className="px-3 py-1 text-white text-sm hover:bg-white/20 rounded-lg transition-colors">
                    +10s
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Likes Panel */}
      {showLikes && (
        <LikesPanel
          likes={content.likes || []}
          currentUserUuid={currentUserUuid}
          isContentOwner={isOwn}
          onToggleLike={onToggleLike}
          onClose={() => setShowLikes(false)}
        />
      )}

      {/* Comments Panel */}
      {showComments && (
        <CommentsPanel
          comments={content.comments || []}
          currentUserUuid={currentUserUuid}
          onAddComment={onAddComment}
          onDeleteComment={onDeleteComment}
          onClose={() => setShowComments(false)}
        />
      )}
    </div>
  );
}

export default VideoPlayer;
